import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionItemButton } from '@components/button/src/action-button';
import { Modal } from '@components/modals';
import {
  ENABLE_MENU_VENUE_OPTION_SETTINGS,
  ENABLE_MENU_VENUE_USER_MANAGEMENT_SETTINGS,
  ENABLED_ADD_VENUE,
} from '@constants/company-auth-permissions';
import {
  ADD_VENUE_ROUTE,
  CLUB_CHANGE_CLUB_ROUTE,
  CLUB_SETTINGS_ROUTE,
  CLUB_USER_MANAGEMENT_SETTINGS,
  SELECT_VENUE_ROUTE,
  USERS_ROUTE,
} from '@constants/routes';
import { XS_VIEWPORT } from '@constants/viewport';
import { AuthContext } from '@contexts/auth/auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { Club } from '@domain/club';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';
import { useWindowSize } from '@hooks/use-window-size';
import { createLegacyUrl } from '@utils/create-legacy-url/create-legacy-url';
import { getOldNightgraphUrlLanguage } from '@utils/get-old-nightgraph-url-language/get-old-nightgraph-url-language';

import { defaultTransitonProps } from '../../utils';

import { MenuVenueOption, MenuVenueRoutes } from './menu-venue.types';
import { MenuVenueButton } from './menu-venue-button';
import { MenuVenueOptions } from './menu-venue-options';
import {
  MENU_VENUE_OPTION_ADD_CLUB,
  MENU_VENUE_OPTION_CHANGE_CLUB,
  MENU_VENUE_OPTION_SETTINGS,
  MENU_VENUE_OPTION_USERS_MANAGEMENT,
} from './utils';
const MenuButtonContent = ({
  isOpen,
  handleShowSubmenu,
  clubState,
}: {
  isOpen: boolean;
  handleShowSubmenu: () => void;
  clubState: Club;
}) => <MenuVenueButton handleShowSubmenu={handleShowSubmenu} showClubSubmenu={isOpen} clubState={clubState} />;

const MenuDesktopOptions = ({
  menuOptions,
  handleClickClub,
}: {
  menuOptions: MenuVenueOption[];
  handleClickClub: (id: MenuVenueRoutes) => void;
}) => (
  <>
    {menuOptions.map((option) => (
      <MenuItem key={`option-menu-desktop-${option.id}`}>
        {({ focus }) => (
          <ActionItemButton active={focus} onClick={() => handleClickClub(option.id)} text={option.label} />
        )}
      </MenuItem>
    ))}
  </>
);

export const MenuVenue = ({
  transitionProps = defaultTransitonProps,
}: {
  transitionProps?: Record<string, string>;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const showMenuVenueButtonSettings = useIsProtectedElement({ permissions: ENABLE_MENU_VENUE_OPTION_SETTINGS });
  const showMenuVenueButtonUserManagement = useIsProtectedElement({
    permissions: ENABLE_MENU_VENUE_USER_MANAGEMENT_SETTINGS,
  });

  const { isSelectVenueActive, isWebView, isUserManagementActive, isAddVenueEnabled } = useContext(FeatureFlagContext);
  const { clubState, companyState } = useContext(CompanyClubContext);
  const { isImpersonated } = useContext(AuthContext);
  const { userState } = useContext(UserContext);
  const viewportLessThanMobile = useWindowSize(XS_VIEWPORT);
  const [showClubSubmenu, setShowClubSubmenu] = useState<boolean>(false);
  const old_nightgraph_locale = getOldNightgraphUrlLanguage(i18n.language);
  const hasAddVenuePermission = useIsProtectedElement({ permissions: ENABLED_ADD_VENUE });
  const menuOptions: MenuVenueOption[] = [
    { id: MENU_VENUE_OPTION_CHANGE_CLUB, label: t(`menu:${MENU_VENUE_OPTION_CHANGE_CLUB}`) },
  ];
  if (!isWebView) {
    if (showMenuVenueButtonSettings)
      menuOptions.splice(0, 0, { id: MENU_VENUE_OPTION_SETTINGS, label: t(`menu:${MENU_VENUE_OPTION_SETTINGS}`) });
  }
  if (isUserManagementActive && showMenuVenueButtonUserManagement)
    menuOptions.push({
      id: MENU_VENUE_OPTION_USERS_MANAGEMENT,
      label: t(`menu:${MENU_VENUE_OPTION_USERS_MANAGEMENT}`),
    });
  if (isAddVenueEnabled && hasAddVenuePermission) {
    menuOptions.push({
      id: MENU_VENUE_OPTION_ADD_CLUB,
      label: t(`menu:${MENU_VENUE_OPTION_ADD_CLUB}`),
    });
  }

  const clubUrls = {
    settings: CLUB_SETTINGS_ROUTE.replace('/:lang', old_nightgraph_locale)
      .replace(':city-slug', clubState.city.slug)
      .replace(':companyId', companyState.legacyId)
      .replace(':club-slug', clubState.slug),
    changeClub: CLUB_CHANGE_CLUB_ROUTE.replace('/:lang', old_nightgraph_locale),
    usersManagement: CLUB_USER_MANAGEMENT_SETTINGS.replace('/:lang', old_nightgraph_locale),
    addClub: CLUB_USER_MANAGEMENT_SETTINGS.replace('/:lang', old_nightgraph_locale),
  };

  const handleClickClub = (route: keyof typeof clubUrls) => {
    if (route === MENU_VENUE_OPTION_ADD_CLUB) {
      navigate(ADD_VENUE_ROUTE.replace(':companyId', companyState.legacyId).replace(':clubSlug', clubState.slug));
    } else if (route === MENU_VENUE_OPTION_CHANGE_CLUB && isSelectVenueActive) {
      navigate(SELECT_VENUE_ROUTE);
    } else if (route === MENU_VENUE_OPTION_USERS_MANAGEMENT) {
      navigate(USERS_ROUTE.replace(':companyId', companyState.legacyId).replace(':clubSlug', clubState.slug));
    } else {
      const url = createLegacyUrl(clubUrls[route], isImpersonated, userState?.email);
      window.open(url, '_self');
    }
  };

  const handleShowSubmenu = () => {
    setShowClubSubmenu(!showClubSubmenu);
  };

  return (
    <Menu>
      {({ open }) => (
        <>
          <MenuButton className='focus-visible:outline-none'>
            {({ open: isOpen }) => (
              <MenuButtonContent isOpen={isOpen} handleShowSubmenu={handleShowSubmenu} clubState={clubState} />
            )}
          </MenuButton>
          {viewportLessThanMobile && (
            <Modal isOpen={showClubSubmenu} handleClose={handleShowSubmenu}>
              <MenuVenueOptions menuOptions={menuOptions} handleClickClub={handleClickClub} />
            </Modal>
          )}
          {!viewportLessThanMobile && (
            <Transition as={Fragment} {...transitionProps} show={open}>
              <MenuItems
                modal={false}
                anchor={{ to: 'bottom end', gap: 16 }}
                className='flex flex-col w-max-content z-10 origin-top-left rounded-md bg-primary shadow-sm p-4 gap-2 focus-visible:outline-none'>
                <p className='text-left text-secondary text-body-xs'>{t('common:optionsLabel')}</p>
                <MenuDesktopOptions menuOptions={menuOptions} handleClickClub={handleClickClub} />
              </MenuItems>
            </Transition>
          )}
        </>
      )}
    </Menu>
  );
};
